import { SOCIAL_LINKING_PLATFORMS } from '@/lib/account-settings/visibleSocialLinkingPlatforms';
import { isMediaType, type MediaType } from '@/lib/social-analytics/mediaTypeKey';

export const ANALYTICS_RANGES = ['D7', 'D30', 'D90', 'ALL'] as const;
export type AnalyticsRange = (typeof ANALYTICS_RANGES)[number];

export const ANALYTICS_SORT_FIELDS = ['published_at', 'views', 'views_gained', 'likes', 'comments', 'shares', 'last_synced_at'] as const;
export type AnalyticsSortField = (typeof ANALYTICS_SORT_FIELDS)[number];

export const ANALYTICS_AVAILABILITY = ['available', 'unavailable', 'deleted'] as const;
export type AnalyticsAvailability = (typeof ANALYTICS_AVAILABILITY)[number];

export const ANALYTICS_PERIODS = ['active', 'published'] as const;
export type AnalyticsPeriod = (typeof ANALYTICS_PERIODS)[number];

export type SortDirection = 'asc' | 'desc';

export type AnalyticsFilters = {
  range: AnalyticsRange;
  platforms: string[];
  accountIds: string[];
  mediaTypes: MediaType[];
  /** null means "the default for the range": published on bounded ranges. Ignored on ALL. */
  period: AnalyticsPeriod | null;
  search: string;
  availability: AnalyticsAvailability | null;
  /** null means "the table's default order for the effective period". */
  sort: AnalyticsSortField | null;
  direction: SortDirection;
  page: number;
};

export const DEFAULT_ANALYTICS_FILTERS: AnalyticsFilters = {
  range: 'ALL',
  platforms: [],
  accountIds: [],
  mediaTypes: [],
  period: null,
  search: '',
  availability: null,
  sort: null,
  direction: 'desc',
  page: 1,
};

/** URL keys owned by /analytics. Every other query parameter is preserved on write. */
const ANALYTICS_URL_KEYS = ['range', 'platforms', 'accounts', 'types', 'period', 'q', 'availability', 'sort', 'dir', 'page'] as const;

// Mirrors the backend limits in normalizeAnalyticsInput (MAX_PAGE, search length).
const MAX_PAGE = 100;
const MAX_SEARCH_LENGTH = 120;
const ACCOUNT_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

const isOneOf = <T extends string>(values: readonly T[], value: unknown): value is T => (
  typeof value === 'string' && (values as readonly string[]).includes(value)
);

export const isAnalyticsSortField = (value: unknown): value is AnalyticsSortField => isOneOf(ANALYTICS_SORT_FIELDS, value);

const parseList = (raw: string | null, isValid: (value: string) => boolean) => {
  if (!raw) return [];
  const values = raw.split(',').map((value) => value.trim()).filter((value) => value && isValid(value));
  return Array.from(new Set(values));
};

/** Reads filters from the URL. Anything invalid falls back to its default and never reaches the backend. */
export function parseAnalyticsFilters(params: URLSearchParams): AnalyticsFilters {
  const range = params.get('range');
  const period = params.get('period');
  const availability = params.get('availability');
  const sort = params.get('sort');
  const page = Number(params.get('page'));
  const validSort = isAnalyticsSortField(sort) ? sort : null;
  return {
    range: isOneOf(ANALYTICS_RANGES, range) ? range : DEFAULT_ANALYTICS_FILTERS.range,
    platforms: parseList(params.get('platforms'), (value) => isOneOf(SOCIAL_LINKING_PLATFORMS, value)),
    accountIds: parseList(params.get('accounts'), (value) => ACCOUNT_ID_PATTERN.test(value)),
    mediaTypes: parseList(params.get('types'), isMediaType) as MediaType[],
    period: isOneOf(ANALYTICS_PERIODS, period) ? period : null,
    search: (params.get('q') ?? '').trim().slice(0, MAX_SEARCH_LENGTH),
    availability: isOneOf(ANALYTICS_AVAILABILITY, availability) ? availability : null,
    sort: validSort,
    direction: validSort && params.get('dir') === 'asc' ? 'asc' : 'desc',
    page: Number.isInteger(page) && page >= 1 && page <= MAX_PAGE ? page : 1,
  };
}

/** Writes filters onto a copy of `base`, leaving out defaults and keeping params the page doesn't own. */
export function serializeAnalyticsFilters(filters: AnalyticsFilters, base: URLSearchParams = new URLSearchParams()): URLSearchParams {
  const next = new URLSearchParams(base);
  ANALYTICS_URL_KEYS.forEach((key) => next.delete(key));
  if (filters.range !== DEFAULT_ANALYTICS_FILTERS.range) next.set('range', filters.range);
  if (filters.platforms.length) next.set('platforms', filters.platforms.join(','));
  if (filters.accountIds.length) next.set('accounts', filters.accountIds.join(','));
  if (filters.mediaTypes.length) next.set('types', filters.mediaTypes.join(','));
  if (filters.period) next.set('period', filters.period);
  const search = filters.search.trim().slice(0, MAX_SEARCH_LENGTH);
  if (search) next.set('q', search);
  if (filters.availability) next.set('availability', filters.availability);
  if (filters.sort) {
    next.set('sort', filters.sort);
    if (filters.direction === 'asc') next.set('dir', 'asc');
  }
  if (filters.page > 1) next.set('page', String(Math.min(filters.page, MAX_PAGE)));
  return next;
}

export type TableQuery = {
  period: AnalyticsPeriod;
  mediaTypes?: MediaType[];
  sort: AnalyticsSortField;
  direction: SortDirection;
  search?: string;
  availability?: AnalyticsAvailability;
  page: number;
};

/** The content table's effective query variables. */
export function resolveTableQuery(filters: AnalyticsFilters): TableQuery {
  // On "All time" both periods select every post, so the switch is hidden and "published" applies.
  const period: AnalyticsPeriod = filters.range === 'ALL' ? 'published' : filters.period ?? 'published';
  const defaultSort: AnalyticsSortField = period === 'active' ? 'views_gained' : 'published_at';
  return {
    period,
    mediaTypes: filters.mediaTypes.length ? filters.mediaTypes : undefined,
    sort: filters.sort ?? defaultSort,
    direction: filters.sort ? filters.direction : 'desc',
    search: filters.search || undefined,
    availability: filters.availability ?? undefined,
    page: filters.page,
  };
}
